<script lang="ts" setup>
import { faq, faqGroups } from '~/data/faq';

usePageSeo('seoFaqTitle', 'seoFaqDescription');

const { t } = useI18n();

// FAQPage JSON-LD, so the answers can appear as rich results. The questions
// only attach to the page node when it is typed FAQPage — nuxt-schema-org's
// question resolver looks the primary WebPage up and skips anything else.
// Passing a computed keeps the graph on the locale being read: switching
// language is a route change into the same component, so a plain array built
// at setup would keep the language the visitor arrived in.
useSchemaOrg(computed(() => [
  defineWebPage({ '@type': 'FAQPage' }),
  ...faq.map(entry => defineQuestion({
    name: t(entry.questionKey),
    acceptedAnswer: t(entry.answerKey),
  })),
]));
</script>

<template>
  <div class="faq-page">
    <Hero
      :title="$t('faqTitle')"
      :subtitle="$t('faqSubtitle')"
      backgroundImage="https://res.cloudinary.com/dlct0dnwt/image/upload/q_auto:eco/v1684614474/refuge/outside/main_yjac0p.jpg"
    />

    <div class="content-container">
      <section
        v-for="group in faqGroups"
        :id="group.id"
        :key="group.id"
        tabindex="-1"
        class="faq-group"
      >
        <h2 class="group-title">{{ $t(group.groupKey) }}</h2>

        <div class="faq-grid">
          <section
            v-for="entry in group.entries"
            :id="entry.id"
            :key="entry.id"
            tabindex="-1"
            class="faq-card"
          >
            <Icon :name="entry.icon" class="faq-icon" aria-hidden="true" />
            <h3 class="faq-question">{{ $t(entry.questionKey) }}</h3>
            <p class="faq-answer">{{ $t(entry.answerKey) }}</p>
          </section>
        </div>
      </section>

      <section class="contact-section">
        <h2 class="contact-title">{{ $t('faqMoreQuestions') }}</h2>
        <p class="contact-text">{{ $t('faqMoreQuestionsText') }}</p>
        <ContactInformation />
      </section>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.faq-page
  width: 100%

.content-container
  max-width: 1300px
  margin: 0 auto
  padding: 0 2rem 8rem

.faq-group
  margin-top: 4rem

  &:focus
    outline: none

.group-title
  font-size: 1.5rem
  font-weight: 800
  text-transform: uppercase
  letter-spacing: 1.5px
  color: $color-primary
  margin-bottom: 2rem

.faq-grid
  display: grid
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr))
  gap: 2rem

.faq-card
  display: flex
  flex-direction: column
  gap: 1rem
  padding: 2rem
  background: white
  border-radius: 20px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05)

  &:focus
    outline: none

.faq-icon
  font-size: 1.75rem
  color: $color-primary

.faq-question
  font-size: 1.2rem
  font-weight: 800
  line-height: 1.4
  margin: 0

.faq-answer
  font-size: 1.05rem
  line-height: 1.7
  opacity: 0.8
  margin: 0

.contact-section
  margin-top: 6rem
  text-align: center

.contact-title
  font-size: 2.5rem
  font-weight: 800
  margin-bottom: 1.25rem

.contact-text
  font-size: 1.25rem
  line-height: 1.6
  opacity: 0.8
  max-width: 800px
  margin: 0 auto 2.5rem

.dark-mode .faq-card
  background: rgba(255, 255, 255, 0.03)
  box-shadow: none
  border: 1px solid rgba(255, 255, 255, 0.05)

@media (max-width: 768px)
  .content-container
    padding: 0 1.5rem 5rem

  .faq-grid
    grid-template-columns: 1fr
    gap: 1.25rem

  .faq-card
    padding: 1.5rem

  .contact-title
    font-size: 2rem
</style>
